import type { StatusTone } from '@vnetwork/ui/data';
import { humanize } from '../status-tones';

/**
 * Email-task status → @ui `StatusPill` tone. The v4 mailer backend is loose
 * about the status vocabulary (sent/delivered/performed/scheduled/pending/
 * bounced/failed and occasional unknowns), so the map is defensive: anything
 * unrecognized falls through to `neutral` and is humanized for display.
 *
 * `receipt_status === 'received'` takes precedence over `mailer_status` — a
 * confirmed receipt is the strongest positive signal (mirrors legacy, which
 * relabels the row "received" when the recipient confirmed).
 */
export function mailerTaskTone(status?: string): StatusTone {
  switch ((status ?? '').toLowerCase()) {
    case 'received':
    case 'sent':
    case 'delivered':
    case 'performed':
    case 'success':
      return 'success';
    case 'scheduled':
    case 'pending':
    case 'queued':
    case 'processing':
      return 'info';
    case 'bounced':
    case 'failed':
    case 'error':
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * Resolve the effective status label for an email-history row: a confirmed
 * receipt wins over the raw send status. Returns `{ status, tone, label }`.
 */
export function resolveMailerTaskStatus(mailerStatus?: string, receiptStatus?: string): {
  status: string;
  tone: StatusTone;
  label: string;
} {
  const status = receiptStatus === 'received' ? 'received' : mailerStatus ?? '';
  return { status, tone: mailerTaskTone(status), label: humanize(status) };
}

/** Strip a namespaced referrer type to its leaf, e.g. `App::MailerCampaign` → `MailerCampaign`. */
export function mailerSource(referrerType?: string): string {
  if (!referrerType) return '—';
  return referrerType.split('::').pop() || referrerType;
}
